import { useState } from "react";
import { useDispatch } from "react-redux";
import { Modal } from "antd";
import {
  HiOutlineCreditCard,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineKey,
  HiOutlineShieldCheck,
  HiOutlineXMark,
} from "react-icons/hi2";
import { billingApi, IByokKeyState, usersApi } from "../../api";
import { IResponseError, IUser } from "../../data-access";
import { storeAuthActions } from "../../store/slices/auth";
import { FormButton } from "../../ui";
import { notify } from "../../util";
import { useUser } from "../../hooks";
import "./byok-key.scss";

interface IByokKeyModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: (state: IByokKeyState) => void;
}

/**
 * Connect / replace the OpenRouter key (Flex plan BYOK). Validates the key
 * against OpenRouter server-side; on success refreshes the current user so
 * every byok consumer (billing card, plan button) picks up the new state.
 */
export const ByokKeyModal = ({ open, onClose, onSaved }: IByokKeyModalProps): JSX.Element => {
  const dispatch = useDispatch();
  const { currentUser } = useUser();
  // The key is write-only (stored encrypted, never sent back) - all we can
  // show for a connected key is its last 4 characters and status.
  const byok = currentUser?.byok;
  const isReplacing = !!byok?.has_key;
  const keyStatus =
    byok?.status === "invalid"
      ? { dot: "red", label: "Invalid" }
      : byok?.status === "no_credits"
        ? { dot: "yellow", label: "No balance" }
        : { dot: "green", label: "Active" };
  const [keyInput, setKeyInput] = useState("");
  // Shown by default so the user can check what they pasted.
  const [isKeyVisible, setIsKeyVisible] = useState(true);
  const [keyError, setKeyError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    const key = keyInput.trim();
    if (!key) {
      setKeyError("Paste your OpenRouter key first.");
      return;
    }
    setIsSaving(true);
    setKeyError("");
    billingApi
      .saveByokKey(key)
      .then((response) => {
        onSaved?.(response);
        setKeyInput("");
        onClose();
        notify(
          "success",
          isReplacing
            ? "OpenRouter key updated."
            : "OpenRouter key connected. AI now runs on your own balance."
        );
        return usersApi.getMe();
      })
      .then((me?: { data: IUser }) => {
        if (me) dispatch(storeAuthActions.setUserData({ user: me.data }));
      })
      .catch((error: IResponseError) => {
        setKeyError(error.text || "OpenRouter rejected this key. Check it and try again.");
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Modal
      open={open}
      className="disconnect-modal"
      closeIcon={<HiOutlineXMark size={20} />}
      onCancel={() => !isSaving && onClose()}
      footer={null}
      title={null}
      width={440}
      destroyOnClose
    >
      <div className="disconnect-modal__content">
        <div className="disconnect-modal__icon disconnect-modal__icon--green">
          <HiOutlineKey size={26} />
        </div>
        <h3 className="disconnect-modal__title">
          {isReplacing ? "Update your OpenRouter key" : "Connect your OpenRouter key"}
        </h3>
        <p className="disconnect-modal__desc">
          {isReplacing ? (
            <>Paste a new key below to replace the connected one. Keys are created at <strong>openrouter.ai</strong> (Settings &rarr; Keys).</>
          ) : (
            <>Create a key at <strong>openrouter.ai</strong> (Settings &rarr; Keys), top up the balance there, and paste it below.</>
          )}
        </p>
        <div className="byok-panel">
          <HiOutlineCreditCard size={16} />
          <div className="byok-panel__body">
            <div className="byok-panel__title">AI will run on your OpenRouter balance</div>
            <div className="byok-panel__row">
              <span className="byok-panel__label">Credit limits</span>
              <span className="byok-panel__value">None - pay OpenRouter directly</span>
            </div>
            <div className="byok-panel__divider" />
            <div className="byok-panel__row">
              <span className="byok-panel__label">If the key fails</span>
              <span className="byok-panel__value">Falls back to 50 plan credits / mo</span>
            </div>
          </div>
        </div>
        {isReplacing && (
          <div className="byok-current">
            <span className="byok-current__label">Connected key</span>
            <span className="byok-current__key">****{byok?.key_last4 ?? ""}</span>
            <span className={`byok-current__status byok-current__status--${keyStatus.dot}`}>
              <span className={`byok-key-btn__dot byok-key-btn__dot--${keyStatus.dot}`} />
              {keyStatus.label}
            </span>
          </div>
        )}
        <div className="byok-field">
          <label className="byok-field__label">{isReplacing ? "New OpenRouter API key" : "OpenRouter API key"}</label>
          <div className="byok-field__control">
            <input
              className="byok-field__input"
              type={isKeyVisible ? "text" : "password"}
              autoComplete="off"
              spellCheck={false}
              placeholder={isReplacing ? "Paste a new key (sk-or-v1-...)" : "sk-or-v1-..."}
              value={keyInput}
              onChange={(e) => { setKeyInput(e.target.value); setKeyError(""); }}
              onKeyDown={(e) => e.key === "Enter" && !isSaving && handleSave()}
            />
            <button
              type="button"
              className="byok-field__toggle"
              onClick={() => setIsKeyVisible((v) => !v)}
              aria-label={isKeyVisible ? "Hide key" : "Show key"}
              title={isKeyVisible ? "Hide key" : "Show key"}
            >
              {/* Icon shows the current state: crossed eye while the key is hidden. */}
              {isKeyVisible ? <HiOutlineEye size={18} /> : <HiOutlineEyeSlash size={18} />}
            </button>
          </div>
          {keyError && <p className="byok-field__error">{keyError}</p>}
        </div>
        <FormButton theme="green-light" onClick={handleSave} loading={isSaving}>
          <HiOutlineShieldCheck size={16} />
          {isReplacing ? "Validate & Update Key" : "Validate & Save Key"}
        </FormButton>
      </div>
    </Modal>
  );
};
